import { supabase } from '@common/api/authService';
import { useEffect } from 'react';

const OUTCOME_SUMMARY_UPDATED_EVENT = 'outcome-summary-updated';

export interface OutcomeSummaryRealtimePayload {
    type?: string;
    workspace_id?: string;
    snapshot_id?: string;
    range_id?: string;
    generated_at?: string;
    reason?: string;
}

export interface UseOutcomeSummaryRealtimeInvalidationParams {
    workspaceId?: string | null;
    selectedRangeId?: string | null;
    enabled?: boolean;
    onCurrentRangeUpdated: (payload: OutcomeSummaryRealtimePayload) => void;
    onOtherRangeUpdated?: (payload: OutcomeSummaryRealtimePayload) => void;
    onError?: (error: Error) => void;
}

export type OutcomeSummaryRealtimePayloadTarget = 'current' | 'other';

export const getOutcomeSummaryRealtimePayloadTarget = ({
    payload,
    workspaceId,
    selectedRangeId,
}: {
    payload?: OutcomeSummaryRealtimePayload;
    workspaceId: string;
    selectedRangeId?: string | null;
}): OutcomeSummaryRealtimePayloadTarget | null => {
    if (!payload || payload.type !== OUTCOME_SUMMARY_UPDATED_EVENT) return null;
    if (payload.workspace_id && payload.workspace_id !== workspaceId) return null;
    if (!selectedRangeId || payload.range_id === selectedRangeId) return 'current';
    return 'other';
};

export const useOutcomeSummaryRealtimeInvalidation = ({
    workspaceId,
    selectedRangeId,
    enabled = true,
    onCurrentRangeUpdated,
    onOtherRangeUpdated,
    onError,
}: UseOutcomeSummaryRealtimeInvalidationParams) => {
    useEffect(() => {
        if (!enabled || !workspaceId) return undefined;

        const topic = `workspace:${workspaceId}:signals`;
        const channel = supabase.channel(topic, { config: { private: true } } as any);

        channel.on(
            'broadcast' as any,
            { event: OUTCOME_SUMMARY_UPDATED_EVENT },
            ({ payload }: { payload?: OutcomeSummaryRealtimePayload }) => {
                const target = getOutcomeSummaryRealtimePayloadTarget({
                    payload,
                    workspaceId,
                    selectedRangeId,
                });
                if (!target || !payload) return;

                if (target === 'current') {
                    onCurrentRangeUpdated(payload);
                    return;
                }

                onOtherRangeUpdated?.(payload);
            },
        );

        channel.subscribe((status) => {
            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                onError?.(new Error(`Outcome Summary realtime subscription ${status.toLowerCase()}`));
            }
        });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [enabled, onCurrentRangeUpdated, onError, onOtherRangeUpdated, selectedRangeId, workspaceId]);
};
